<template>
  <div v-if="visible">
    <button type="button" class="btn btn-outline-primary btn-sm text-capitalize-first" @click="openPopup">
      {{ actionLabel }}
    </button>

    <Popup
      v-if="!bootstrapModal"
      ref="linkPopup"
      class="popup--narrow"
      :title="$t('labels.manual_sbp_qr_deal_image')"
      @onClose="hidePopup"
    >
      <div class="popup__content">
        <div class="form-group mb-3">
          <input
            v-model="link"
            type="text"
            class="form-control mb-3"
            autocomplete="off"
            :placeholder="$t('labels.manual_sbp_qr_deal_link_placeholder')"
          />
          <DropzoneInput
            :options="dropzoneOptions"
            label="labels.qr_upload_file"
            @change="onImageChange"
            @reject="onImageReject"
          />
          <img v-if="previewUrl" :src="previewUrl" alt="" class="manual-sbp-qr-preview mt-3" />
          <button
            v-if="previewUrl"
            type="button"
            class="btn btn-link btn-sm px-0 mt-1"
            @click.prevent="clearImage"
          >
            {{ $t('button.delete') }}
          </button>
        </div>
        <small v-if="localError" class="text-danger d-block mb-2">{{ localError }}</small>
        <div class="d-flex justify-content-end gap-2 flex-nowrap">
          <button type="button" class="btn btn-outline-dark btn-sm text-nowrap" @click.prevent="hidePopup">
            <span class="text-capitalize-first">{{ $t('button.cancel') }}</span>
          </button>
          <LoaderButton :on-click="save" class="btn btn-primary btn-sm text-nowrap">
            <span class="text-capitalize-first">{{ $t('button.save') }}</span>
          </LoaderButton>
        </div>
      </div>
    </Popup>

    <BModal
      v-else
      :model-value="bsOpen"
      :title="$t('labels.manual_sbp_qr_deal_image')"
      footer-class="d-flex justify-content-end gap-2 flex-nowrap"
      @update:model-value="onBsOpenChange"
    >
      <template #default>
        <div class="form-group mb-3">
          <input
            v-model="link"
            type="text"
            class="form-control mb-3"
            autocomplete="off"
            :placeholder="$t('labels.manual_sbp_qr_deal_link_placeholder')"
          />
          <DropzoneInput
            :options="dropzoneOptions"
            label="labels.qr_upload_file"
            @change="onImageChange"
            @reject="onImageReject"
          />
          <img v-if="previewUrl" :src="previewUrl" alt="" class="manual-sbp-qr-preview mt-3" />
          <button
            v-if="previewUrl"
            type="button"
            class="btn btn-link btn-sm px-0 mt-1"
            @click.prevent="clearImage"
          >
            {{ $t('button.delete') }}
          </button>
        </div>
        <small v-if="localError" class="text-danger d-block">{{ localError }}</small>
      </template>
      <template #footer>
        <button type="button" class="btn btn-outline-secondary btn-sm text-nowrap text-capitalize-first" @click.prevent="hidePopup">
          {{ $t('button.cancel') }}
        </button>
        <LoaderButton :on-click="save" class="btn btn-primary btn-sm text-nowrap">
          <span class="text-capitalize-first">{{ $t('button.save') }}</span>
        </LoaderButton>
      </template>
    </BModal>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, ref} from 'vue'
import {useStore} from 'vuex'
import LoaderButton from '@/apps/public/components/LoaderButton.vue'
import Popup from '@/apps/public/components/Popup.vue'
import DropzoneInput from '@/apps/public/components/DropzoneInput.vue'
import {$t, helper} from '@/service/CommonHelper'
import {api} from '@/service/APIClient'
import {readFileAsDataUrl} from '@/service/vietQrRawHelpers'
import TraderDealDTO from '@/models/TraderDealDTO'
import {Direction} from '@/models/enum/Direction'
import {DealStatus} from '@/models/enum/DealStatus'
import {PaymentOption} from '@/models/enum/PaymentOption'
import {BModal} from 'bootstrap-vue-3'

const dropzoneOptions = {
  accept: ['.png', '.jpg', '.jpeg', '.webp', 'image/png', 'image/jpeg', 'image/webp'],
  multiple: false,
  maxFiles: 1,
}

export default defineComponent({
  name: 'ManualSbpQrLinkEditor',
  components: {LoaderButton, Popup, BModal, DropzoneInput},
  props: {
    deal: {
      type: Object as PropType<TraderDealDTO>,
      required: true,
    },
    bootstrapModal: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['applied'],
  setup(props, {emit}) {
    const store = useStore()
    const link = ref('')
    const imageDataUri = ref<string | null>(null)
    const previewUrl = ref<string | null>(null)
    const localError = ref<string | null>(null)
    const linkPopup = ref<InstanceType<typeof Popup> | null>(null)
    const bsOpen = ref(false)

    const visible = computed(() => {
      const d = props.deal
      if (!d) {
        return false
      }
      if (!(store.getters.isTrader || store.getters.isTraderOperator || store.getters.isTeamLead)) {
        return false
      }
      if (d.type !== Direction.PAY_IN) {
        return false
      }
      if (d.paymentOption !== PaymentOption.MANUAL_SBP_QR) {
        return false
      }
      return d.status === DealStatus.TRANSFER_WAITING
    })

    const savedLink = computed(() => helper.dealQrCodeLink(props.deal))

    const syncFromDeal = () => {
      link.value = savedLink.value ?? ''
      imageDataUri.value = null
      previewUrl.value = null
      localError.value = null
    }

    const actionLabel = computed(() => {
      return (
        savedLink.value
          ? ($t('button.edit') as string)
          : ($t('labels.manual_sbp_qr_attach_image') as string)
      )
    })

    const hidePopup = () => {
      bsOpen.value = false
      linkPopup.value?.hide()
      syncFromDeal()
    }

    const openPopup = () => {
      syncFromDeal()
      if (props.bootstrapModal) {
        bsOpen.value = true
      } else {
        linkPopup.value?.show()
      }
    }

    const onBsOpenChange = (open: boolean) => {
      bsOpen.value = open
      if (!open) {
        syncFromDeal()
      }
    }

    const clearImage = () => {
      imageDataUri.value = null
      previewUrl.value = null
    }

    const onImageReject = () => {
      imageDataUri.value = null
      previewUrl.value = null
      localError.value = $t('labels.manual_sbp_qr_image_type') as string
    }

    const onImageChange = async (files: File[]) => {
      const file = files?.[0]
      if (!file) {
        return
      }
      localError.value = null
      try {
        const dataUri = await readFileAsDataUrl(file)
        imageDataUri.value = dataUri
        previewUrl.value = dataUri
      } catch {
        imageDataUri.value = null
        previewUrl.value = null
        localError.value = $t('labels.manual_sbp_qr_image_type') as string
      }
    }

    const mapSaveError = (e: any): string => {
      if (helper.isValidationErrors(e)) {
        const first = helper.getValidationErrors(e)[0]
        const msg = `${first?.message ?? ''}`.trim().toLowerCase()
        if ((msg === 'invalid link' || msg.includes('invalid link')) && first?.field === 'image') {
          return $t('labels.manual_sbp_qr_image_unreadable') as string
        }
        if (msg.includes('provide either link or image')) {
          return $t('labels.manual_sbp_qr_payload_required') as string
        }
        return first?.message ?? $t('server_errors.INTERNAL_ERROR') as string
      }
      return e?.response?.data?.message ?? ($t('server_errors.INTERNAL_ERROR') as string)
    }

    const save = async () => {
      localError.value = null
      const image = imageDataUri.value
      const trimmed = link.value.trim()
      let payload: {image: string} | {link: string}
      if (image) {
        payload = {image}
      } else if (trimmed) {
        const clientErr = helper.manualSbpQrLinkError(trimmed)
        if (clientErr) {
          localError.value = clientErr
          return
        }
        payload = {link: trimmed}
      } else {
        localError.value = $t('labels.manual_sbp_qr_payload_required') as string
        return
      }
      try {
        const updated = await api.dealManualSbpQrLink(props.deal.id, payload)
        emit('applied', updated)
        hidePopup()
      } catch (e: any) {
        localError.value = mapSaveError(e)
      }
    }

    return {
      visible,
      link,
      previewUrl,
      localError,
      dropzoneOptions,
      save,
      onImageChange,
      onImageReject,
      clearImage,
      openPopup,
      hidePopup,
      linkPopup,
      bsOpen,
      onBsOpenChange,
      actionLabel,
    }
  },
})
</script>

<style scoped>
.manual-sbp-qr-preview {
  display: block;
  max-width: 220px;
  height: auto;
  border-radius: 10px;
}
</style>
